<template>
  <div class="p-6">
    <!-- Header Section -->
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
        Approval Changes
        <span v-if="isFiltered" class="text-sm text-gray-500">(filtered)</span>
      </h1>
      <div class="flex gap-4">
        <div class="relative group">
        <button
          type="button"
          class="hover:bg-transparent bg-transparent"
          @click="startTutorial"
        >
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
        </button>
          <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 glass-card mb-1 px-2 py-0.5 glass-card opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
        </div>
      </div>
    </div>


    <!-- Organization Tabs -->
    <OrganizationTabs
      v-if="user?.is_staff || isServiceProviderUser"
      :organizations="tenant.organizations.value"
      :selected-org-id="tenant.currentOrganizationId.value"
      :loading="tenant.isLoading.value"
      :show-tabs="true"
      loading-text="Loading approval changes..."
      @select="handleOrgSelect"
    />
    <div
      v-if="configStatus && configStatus !== 'abandoned' && configStatus !== 'failed'"
      class="mb-4 p-4 rounded-xl backdrop-blur-xl border shadow-2xl"
      :class="{
        'bg-blue-400/20 text-blue-800 dark:text-blue-300 border-white/40 shadow-blue-500/20':
          configStatus === 'pending' || configStatus === 'queued' || configStatus === 'running',
        'bg-green-400/20 text-green-800 dark:text-green-300 border-white/40 shadow-green-500/20':
          configStatus === 'completed',
      }"
    >
      <p class="font-semibold flex items-center">
        <span class="mr-2">Configuration Status:</span>
        <span class="capitalize">{{ configStatus }}</span>
      </p>
    </div>

    <!-- Abandoned / Failed banner with retry + dismiss actions -->
    <div
      v-if="configStatus === 'abandoned' || configStatus === 'failed'"
      class="mb-4 p-4 rounded-xl backdrop-blur-xl border shadow-2xl bg-amber-400/20 text-amber-900 dark:text-amber-200 border-white/40 shadow-amber-500/20"
    >
      <p class="font-semibold mb-2">
        <span class="capitalize">{{ configStatus }}</span> commit
        <span v-if="latestFileRequest?.created_date">
          ({{ new Date(latestFileRequest.created_date).toLocaleString() }})
        </span>
      </p>
      <p class="text-sm mb-3">
        <template v-if="configStatus === 'abandoned'">
          The previous commit never reported back. The worker likely died or the task was
          never dispatched. Approve buttons are unblocked; click Dismiss to clear this
          notice, or Retry to re-run the commit.
        </template>
        <template v-else>
          The previous commit failed. See the message below. Approve buttons are unblocked;
          click Dismiss to clear this notice, or Retry to re-run.
        </template>
      </p>
      <pre
        v-if="latestFileRequest?.error_message"
        class="text-xs whitespace-pre-wrap bg-black/10 dark:bg-white/10 rounded p-2 mb-3 max-h-32 overflow-auto"
      >{{ latestFileRequest.error_message }}</pre>
      <div class="flex gap-2">
        <button
          @click="dismissLatestRequest"
          :disabled="isDismissing"
          class="px-3 py-1.5 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
        >
          {{ isDismissing ? "Dismissing..." : "Dismiss" }}
        </button>
        <button
          @click="retryLatestRequest"
          :disabled="isLoading"
          class="px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          Retry Commit
        </button>
      </div>
    </div>

    <!-- Error Message -->
    <div
      v-if="error"
      class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
      role="alert"
    >
      <strong class="font-bold">Error!</strong>
      <span class="block sm:inline">{{ error }}</span>
    </div>

    <!-- Loading Message -->
    <div v-if="isLoading" class="text-gray-600 dark:text-gray-50 font-medium mb-4">
      <svg
        class="animate-spin h-5 w-5 mr-2 inline-block text-yellow-600 dark:text-neutral-50"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        ></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 2.21.896 4.21 2.342 5.658l1.658-1.367z"
        ></path>
      </svg>
      Loading salt state preview...
    </div>

    <!-- Search Bar -->
    <div class="mb-4">
      <SearchBar
        v-model="searchQuery"
        placeholderText="Search by module name..."
      />
    </div>

    <!-- Tabs -->
    <div v-if="!isLoading && !error" class="mb-4">
      <div id="config-tabs" class="my-2 backdrop-blur-sm py-3 px-3 glass-card-lite">
        <div class="flex items-center">
          <button
            type="button"
            @click="scrollTabsByPage(-1)"
            :disabled="!canScrollTabsPrev"
            aria-label="Scroll tabs left"
            class="group bg-transparent hover:bg-transparent p-1 disabled:opacity-30 disabled:cursor-not-allowed disabled:pointer-events-none transition-opacity duration-200"
          >
            <ChevronLeft class="h-6 w-6 text-gray-900 dark:text-white transition-all duration-200 group-hover:text-fuchsia-500 group-hover:-translate-x-1 group-active:scale-90" />
          </button>

          <nav
            ref="tabNav"
            @scroll="onTabsScroll"
            class="-mb-px flex space-x-8 overflow-x-auto flex-1 no-scrollbar scroll-smooth"
            aria-label="Tabs"
          >
            <template v-for="tab in tabs" :key="tab.id">
              <button
                v-if="orgModules.includes(tab.id)"
                @click="activeTab = tab.id"
                :class="[
                'w-1/3 px-4 py-2 font-bold transition-all duration-200 flex text-center items-center space-x-2 rounded-md border border-white glass-card whitespace-nowrap',
                activeTab === tab.id
                    ? 'bg-transparent text-fuchsia-500'
                    : 'text-gray-400 hover:bg-slate-800/30 hover:text-fuchsia-500 bg-transparent'
                ]"
                :aria-selected="activeTab === tab.id"
                :aria-controls="`${tab.id}-panel`"
              >
                {{ tab.name }} ({{ totalItems[tab.id] || 0 }})
              </button>
            </template>
          </nav>

          <button
            type="button"
            @click="scrollTabsByPage(1)"
            :disabled="!canScrollTabsNext"
            aria-label="Scroll tabs right"
            class="group bg-transparent hover:bg-transparent p-1 disabled:opacity-30 disabled:cursor-not-allowed disabled:pointer-events-none transition-opacity duration-200"
          >
            <ChevronRight class="h-6 w-6 text-gray-900 dark:text-white transition-all duration-200 group-hover:text-fuchsia-500 group-hover:translate-x-1 group-active:scale-90" />
          </button>
        </div>
      </div>

      <div v-if="tabsMarkerCount > 1" class="flex justify-center items-center gap-1.5 mt-2" aria-hidden="true">
        <span
          v-for="idx in tabsMarkerCount"
          :key="idx"
          :class="[
            'tabs-scroll-marker',
            tabsCurrentMarker === idx - 1 ? 'tabs-scroll-marker--active' : ''
          ]"
        ></span>
      </div>
    </div>

    <!-- Selection Status and Clear Button -->
    <div
      v-if="!isLoading && !error"
      class="mb-4 flex justify-between items-center"
    >
      <div class="text-sm text-gray-600 dark:text-gray-50">
        <span v-if="activeTab === 'probes'">
          {{ selectedProbes.length }} probe{{
            selectedProbes.length !== 1 ? "s" : ""
          }}
          selected
        </span>
        <span v-else-if="activeTab === 'sites'">
          {{ selectedSites.length }} site{{
            selectedSites.length !== 1 ? "s" : ""
          }}
          selected
        </span>
        <span v-else-if="activeTab === 'loadsheddingareas'">
          {{ selectedLoadsheddingAreas.length }} loadsheddingarea{{
            selectedLoadsheddingAreas.length !== 1 ? "s" : ""
          }}
          selected
        </span>
        <span v-else-if="activeTab === 'blackboxes'">
          {{ selectedBlackboxes.length }} endpoint availability{{
            selectedBlackboxes.length !== 1 ? "s" : ""
          }}
          selected
        </span>
        <span v-else-if="activeTab === 'alloys'">
          {{ selectedAlloys.length }} collector config{{
            selectedAlloys.length !== 1 ? "s" : ""
          }}
          selected
        </span>
        <span v-else-if="activeTab === 'wifi_profiles'">
          {{ selectedWifiProfiles.length }} wifi profile{{
            selectedWifiProfiles.length !== 1 ? "s" : ""
          }}
          selected
        </span>
        <span v-else-if="activeTab === 'captures'">
          {{ selectedCaptures.length }} capture config{{
            selectedCaptures.length !== 1 ? "s" : ""
          }}
          selected
        </span>
        <span v-else-if="activeTab === 'ssh_collectors'">
          {{ selectedSshCollectors.length }} ssh collector config{{
            selectedSshCollectors.length !== 1 ? "s" : ""
          }}
          selected
        </span>
        <span v-else-if="activeTab === 'host_availabilities'">
          {{ selectedHostAvailabilities.length }} host availability{{
            selectedHostAvailabilities.length !== 1 ? "s" : ""
          }}
          selected
        </span>
        <span v-else-if="activeTab === 'network_devices'">
          {{ selectedNetworkDevices.length }} network device{{
            selectedNetworkDevices.length !== 1 ? "s" : ""
          }}
          selected
        </span>
        <span v-else-if="activeTab === 'all'">
          {{ selectedAll.length }} item{{
            selectedAll.length !== 1 ? "s" : ""
          }}
          selected
        </span>
        <span v-else-if="activeTab === 'agents'">
          {{ selectedAgents.length }} agent{{
            selectedAgents.length !== 1 ? "s" : ""
          }}
          selected
        </span>
        <span v-else-if="activeTab === 'binaries'">
          {{ selectedBinaries.length }} binaries{{
            selectedBinaries.length !== 1 ? "s" : ""
          }}
          selected
        </span>
        <span v-else>
          {{ selectedTunnels.length }} tunnel{{
            selectedTunnels.length !== 1 ? "s" : ""
          }}
          selected
        </span>
      </div>

      <button
        v-if="
          (activeTab === 'probes' && selectedProbes.length > 0) ||
          (activeTab === 'sites' && selectedSites.length > 0) ||
          (activeTab === 'tunnels' && selectedTunnels.length > 0) ||
          (activeTab === 'loadsheddingareas' && selectedLoadsheddingAreas.length > 0) ||
          (activeTab === 'blackboxes' && selectedBlackboxes.length > 0) ||
          (activeTab === 'alloys' && selectedAlloys.length > 0) ||
          (activeTab === 'wifi_profiles' && selectedWifiProfiles.length > 0) ||
          (activeTab === 'captures' && selectedCaptures.length > 0) ||
          (activeTab === 'ssh_collectors' && selectedSshCollectors.length > 0) ||
          (activeTab === 'host_availabilities' && selectedHostAvailabilities.length > 0) ||
          (activeTab === 'network_devices' && selectedNetworkDevices.length > 0) ||
          (activeTab === 'agents' && selectedAgents.length > 0) ||
          (activeTab === 'binaries' && selectedBinaries.length > 0) ||
          (activeTab === 'all' && selectedAll.length > 0)
        "
        @click="clearSelection"
        class="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 transition-colors"
        aria-label="Clear selection"
      >
        Clear Selection
      </button>
    </div>

    <!-- Salt State Preview Tables -->
    <div v-if="!isLoading && !error">

      <div
        v-show="activeTab === 'all' && orgModules.includes('all')"
        :id="'all-panel'"
        role="tabpanel"
        aria-labelledby="all-tab"
      >
        <GenericTable
          :columns="allColumns"
          :items="paginatedAll"
          itemKey="id"
          :selectable="true"
          v-model:selected="selectedAll"
          @sort="sortItems('all')"
          @row-click="handleRowClick"
          class="cursor-pointer"
        >
          <template #last_modified="{ value }">
            {{ formatDateTime(value) }}
          </template>
          <template #header-select>
            <input
              type="checkbox"
              :checked="isAllSelected"
              @change="toggleAll"
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              aria-label="Select all blackboxes"
            />
          </template>
          <template #cell-select="{ item }">
            <input
              type="checkbox"
              :value="item"
              v-model="selectedAll"
              @click.stop
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              :aria-label="`Select ${item.name}`"
            />
          </template>
        </GenericTable>
      </div>   

      <!-- Probes Table -->
      <div
        v-show="activeTab === 'probes' && orgModules.includes('probes')"
        :id="'probes-panel'"
        role="tabpanel"
        aria-labelledby="probes-tab"
      >
        <GenericTable
          :columns="probeColumns"
          :items="paginatedProbes"
          itemKey="id"
          :selectable="true"
          v-model:selected="selectedProbes"
          @sort="sortItems('probes')"
          @row-click="handleRowClick"
          class="cursor-pointer"
        >
          <template #last_modified="{ value }">
            {{ formatDateTime(value) }}
          </template>
          <template #header-select>
            <input
              type="checkbox"
              :checked="isAllProbesSelected"
              @change="toggleAllProbes"
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              aria-label="Select all probes"
            />
          </template>
          <template #cell-select="{ item }">
            <input
              type="checkbox"
              :value="item"
              v-model="selectedProbes"
              @click.stop
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              :aria-label="`Select ${item.name}`"
            />
          </template>
        </GenericTable>
      </div>

      <!-- Sites Table -->
      <div
        v-show="activeTab === 'sites' && orgModules.includes('sites')"
        :id="'sites-panel'"
        role="tabpanel"
        aria-labelledby="sites-tab"
      >
        <GenericTable
          :columns="siteColumns"
          :items="paginatedSites"
          itemKey="id"
          :selectable="true"
          v-model:selected="selectedSites"
          @sort="sortItems('sites')"
          @row-click="handleRowClick"
          class="cursor-pointer"
        >
          <template #last_modified="{ value }">
            {{ formatDateTime(value) }}
          </template>
          <template #header-select>
            <input
              type="checkbox"
              :checked="isAllSitesSelected"
              @change="toggleAllSites"
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              aria-label="Select all sites"
            />
          </template>
          <template #cell-select="{ item }">
            <input
              type="checkbox"
              :value="item"
              v-model="selectedSites"
              @click.stop
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              :aria-label="`Select ${item.name}`"
            />
          </template>
        </GenericTable>
      </div>

      <!-- Tunnels Table -->
      <div
        v-show="activeTab === 'tunnels' && orgModules.includes('tunnels')"
        :id="'tunnels-panel'"
        role="tabpanel"
        aria-labelledby="tunnels-tab"
      >
        <GenericTable
          :columns="tunnelColumns"
          :items="paginatedTunnels"
          itemKey="id"
          :selectable="true"
          v-model:selected="selectedTunnels"
          @sort="sortItems('tunnels')"
          @row-click="handleRowClick"
          class="cursor-pointer"
        >
          <template #last_modified="{ value }">
            {{ formatDateTime(value) }}
          </template>
          <template #header-select>
            <input
              type="checkbox"
              :checked="isAllTunnelsSelected"
              @change="toggleAllTunnels"
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              aria-label="Select all tunnels"
            />
          </template>
          <template #cell-select="{ item }">
            <input
              type="checkbox"
              :value="item"
              v-model="selectedTunnels"
              @click.stop
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              :aria-label="`Select ${item.name}`"
            />
          </template>
        </GenericTable>
      </div>

      <!-- LD Table -->
      <div
        v-show="activeTab === 'loadsheddingareas' && orgModules.includes('loadsheddingareas')"
        :id="'loadsheddingareas-panel'"
        role="tabpanel"
        aria-labelledby="loadsheddingareas-tab"
      >
        <GenericTable
          :columns="loadsheddingareaColumns"
          :items="paginatedLoadsheddingAreas"
          itemKey="id"
          :selectable="true"
          v-model:selected="selectedLoadsheddingAreas"
          @sort="sortItems('loadsheddingareas')"
          @row-click="handleRowClick"
          class="cursor-pointer"
        >
          <template #last_modified="{ value }">
            {{ formatDateTime(value) }}
          </template>
          <template #header-select>
            <input
              type="checkbox"
              :checked="isAllLoadsheddingAreasSelected"
              @change="toggleAllLoadSheddingAreas"
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              aria-label="Select all loadsheddingareas"
            />
          </template>
          <template #cell-select="{ item }">
            <input
              type="checkbox"
              :value="item"
              v-model="selectedLoadsheddingAreas"
              @click.stop
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              :aria-label="`Select ${item.name}`"
            />
          </template>
        </GenericTable>
      </div>

      <!-- EA Table -->
      <div
        v-show="activeTab === 'blackboxes' && orgModules.includes('blackboxes')"
        :id="'blackboxes-panel'"
        role="tabpanel"
        aria-labelledby="blackboxes-tab"
      >
        <GenericTable
          :columns="blackboxColumns"
          :items="paginatedBlackboxes"
          itemKey="id"
          :selectable="true"
          v-model:selected="selectedBlackboxes"
          @sort="sortItems('blackboxes')"
          @row-click="handleRowClick"
          class="cursor-pointer"
        >
          <template #last_modified="{ value }">
            {{ formatDateTime(value) }}
          </template>
          <template #header-select>
            <input
              type="checkbox"
              :checked="isAllBlackboxesSelected"
              @change="toggleAllBlackboxes"
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              aria-label="Select all blackboxes"
            />
          </template>
          <template #cell-select="{ item }">
            <input
              type="checkbox"
              :value="item"
              v-model="selectedBlackboxes"
              @click.stop
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              :aria-label="`Select ${item.name}`"
            />
          </template>
        </GenericTable>
      </div>

      <!-- Host Availability Table -->
      <div
        v-show="activeTab === 'host_availabilities' && orgModules.includes('host_availabilities')"
        :id="'host_availabilities-panel'"
        role="tabpanel"
        aria-labelledby="host_availabilities-tab"
      >
        <GenericTable
          :columns="hostAvailabilityColumns"
          :items="paginatedHostAvailabilities"
          itemKey="id"
          :selectable="true"
          v-model:selected="selectedHostAvailabilities"
          @sort="sortItems('host_availabilities')"
          @row-click="handleRowClick"
          class="cursor-pointer"
        >
          <template #last_modified="{ value }">
            {{ formatDateTime(value) }}
          </template>
          <template #header-select>
            <input
              type="checkbox"
              :checked="isAllHostAvailabilitiesSelected"
              @change="toggleAllHostAvailabilities"
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              aria-label="Select all host availabilities"
            />
          </template>
          <template #cell-select="{ item }">
            <input
              type="checkbox"
              :value="item"
              v-model="selectedHostAvailabilities"
              @click.stop
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              :aria-label="`Select ${item.job_name}`"
            />
          </template>
        </GenericTable>
      </div>

      <!-- Network Devices Table -->
      <div
        v-show="activeTab === 'network_devices' && orgModules.includes('network_devices')"
        :id="'network_devices-panel'"
        role="tabpanel"
        aria-labelledby="network_devices-tab"
      >
        <GenericTable
          :columns="networkDeviceColumns"
          :items="paginatedNetworkDevices"
          itemKey="id"
          :selectable="true"
          v-model:selected="selectedNetworkDevices"
          @sort="sortItems('network_devices')"
          @row-click="handleRowClick"
          class="cursor-pointer"
        >
          <template #last_modified="{ value }">
            {{ formatDateTime(value) }}
          </template>
          <template #header-select>
            <input
              type="checkbox"
              :checked="isAllNetworkDevicesSelected"
              @change="toggleAllNetworkDevices"
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              aria-label="Select all network devices"
            />
          </template>
          <template #cell-select="{ item }">
            <input
              type="checkbox"
              :value="item"
              v-model="selectedNetworkDevices"
              @click.stop
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              :aria-label="`Select ${item.name}`"
            />
          </template>
        </GenericTable>
      </div>

      <!-- Alloy Configs Table -->
      <div
        v-show="activeTab === 'alloys' && orgModules.includes('alloys')"
        :id="'alloys-panel'"
        role="tabpanel"
        aria-labelledby="alloys-tab"
      >
        <GenericTable
          :columns="alloyColumns"
          :items="paginatedAlloys"
          itemKey="id"
          :selectable="true"
          v-model:selected="selectedAlloys"
          @sort="sortItems('alloys')"
          @row-click="handleRowClick"
          class="cursor-pointer"
        >
          <template #last_modified="{ value }">
            {{ formatDateTime(value) }}
          </template>
          <template #header-select>
            <input
              type="checkbox"
              :checked="isAllAlloysSelected"
              @change="toggleAllAlloys"
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              aria-label="Select all alloy configs"
            />
          </template>
          <template #cell-select="{ item }">
            <input
              type="checkbox"
              :value="item"
              v-model="selectedAlloys"
              @click.stop
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              :aria-label="`Select ${item.name}`"
            />
          </template>
        </GenericTable>
      </div>

      <!-- WiFi Profiles Table -->
      <div
        v-show="activeTab === 'wifi_profiles' && orgModules.includes('wifi_profiles')"
        :id="'wifi_profiles-panel'"
        role="tabpanel"
        aria-labelledby="wifi_profiles-tab"
      >
        <GenericTable
          :columns="wifiProfileColumns"
          :items="paginatedWifiProfiles"
          itemKey="id"
          :selectable="true"
          v-model:selected="selectedWifiProfiles"
          @sort="sortItems('wifi_profiles')"
          @row-click="handleRowClick"
          class="cursor-pointer"
        >
          <template #last_modified="{ value }">
            {{ formatDateTime(value) }}
          </template>
          <template #header-select>
            <input
              type="checkbox"
              :checked="isAllWifiProfilesSelected"
              @change="toggleAllWifiProfiles"
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              aria-label="Select all wifi profiles"
            />
          </template>
          <template #cell-select="{ item }">
            <input
              type="checkbox"
              :value="item"
              v-model="selectedWifiProfiles"
              @click.stop
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              :aria-label="`Select ${item.name}`"
            />
          </template>
        </GenericTable>
      </div>

      <!-- Captures Table -->
      <div
        v-show="activeTab === 'captures' && orgModules.includes('captures')"
        :id="'captures-panel'"
        role="tabpanel"
        aria-labelledby="captures-tab"
      >
        <GenericTable
          :columns="captureColumns"
          :items="paginatedCaptures"
          itemKey="id"
          :selectable="true"
          v-model:selected="selectedCaptures"
          @sort="sortItems('captures')"
          @row-click="handleRowClick"
          class="cursor-pointer"
        >
          <template #last_modified="{ value }">
            {{ formatDateTime(value) }}
          </template>
          <template #header-select>
            <input
              type="checkbox"
              :checked="isAllCapturesSelected"
              @change="toggleAllCaptures"
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              aria-label="Select all captures"
            />
          </template>
          <template #cell-select="{ item }">
            <input
              type="checkbox"
              :value="item"
              v-model="selectedCaptures"
              @click.stop
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              :aria-label="`Select ${item.name}`"
            />
          </template>
        </GenericTable>
      </div>

      <!-- SSH Runners Table -->
      <div
        v-show="activeTab === 'ssh_collectors' && orgModules.includes('ssh_collectors')"
        :id="'ssh_collectors-panel'"
        role="tabpanel"
        aria-labelledby="ssh_collectors-tab"
      >
        <GenericTable
          :columns="sshCollectorColumns"
          :items="paginatedSshCollectors"
          itemKey="id"
          :selectable="true"
          v-model:selected="selectedSshCollectors"
          @sort="sortItems('ssh_collectors')"
          @row-click="handleRowClick"
          class="cursor-pointer"
        >
          <template #last_modified="{ value }">
            {{ formatDateTime(value) }}
          </template>
          <template #header-select>
            <input
              type="checkbox"
              :checked="isAllSshCollectorsSelected"
              @change="toggleAllSshCollectors"
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              aria-label="Select all SSH collector configs"
            />
          </template>
          <template #cell-select="{ item }">
            <input
              type="checkbox"
              :value="item"
              v-model="selectedSshCollectors"
              @click.stop
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              :aria-label="`Select ${item.name}`"
            />
          </template>
        </GenericTable>
      </div>

      <!--Agents Table -->
      <div
        v-show="activeTab === 'agents'"
        :id="'agents-panel'"
        role="tabpanel"
        aria-labelledby="agents-tab"
      >
        <GenericTable
          :columns="agentColumns"
          :items="paginatedAgents"
          itemKey="id"
          :selectable="true"
          v-model:selected="selectedAgents"
          @sort="sortItems('agents')"
          @row-click="handleRowClick"
          class="cursor-pointer"
        >
          <template #last_modified="{ value }">
            {{ formatDateTime(value) }}
          </template>
          <template #header-select>
            <input
              type="checkbox"
              :checked="isAllAgentsSelected"
              @change="toggleAllAgents"
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              aria-label="Select all agents"
            />
          </template>
          <template #cell-select="{ item }">
            <input
              type="checkbox"
              :value="item"
              v-model="selectedAgents"
              @click.stop
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              :aria-label="`Select ${item.name}`"
            />
          </template>
        </GenericTable>
      </div>


      <!--Binaries Table -->
      <div
        v-show="activeTab === 'binaries'"
        :id="'agents-panel'"
        role="tabpanel"
        aria-labelledby="agents-tab"
      >
        <GenericTable
          :columns="binaryColumns"
          :items="paginatedBinaries"
          itemKey="id"
          :selectable="true"
          v-model:selected="selectedBinaries"
          @sort="sortItems('binaries')"
          @row-click="handleRowClick"
          class="cursor-pointer"
        >
          <template #last_modified="{ value }">
            {{ formatDateTime(value) }}
          </template>
          <template #header-select>
            <input
              type="checkbox"
              :checked="isAllBinariesSelected"
              @change="toggleAllBinaries"
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              aria-label="Select all agents"
            />
          </template>
          <template #cell-select="{ item }">
            <input
              type="checkbox"
              :value="item"
              v-model="selectedBinaries"
              @click.stop
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              :aria-label="`Select ${item.name}`"
            />
          </template>
        </GenericTable>
      </div>
    </div>

    <!-- Pagination Section -->
    <div
      class="flex flex-col md:flex-row items-center justify-between mt-8 space-y-4 md:space-y-0"
    >
      <div class="flex items-center space-x-2">
        <button
          @click="changePage(-1)"
          :disabled="currentPage === 1"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
          aria-label="Previous page"
        >
          Previous
        </button>
        <button
          @click="changePage(1)"
          :disabled="currentPage === totalPages[activeTab]"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
          aria-label="Next page"
        >
          Next
        </button>
      </div>
      <div class="text-gray-700">
        Page {{ currentPage }} of {{ totalPages[activeTab] }}
      </div>
      <div>
        <GlassSelect
          v-model="itemsPerPage"
          :options="pageSizeOptions"
          valueKey="value"
          labelKey="label"
          placeholder="Page size"
          class="!w-36"
          @change="updatePagination"
        />
      </div>
    </div>

    <!-- Actions Section -->
    <div id="config-actions" class="mt-8 flex space-x-4">
      <button v-if="isStaff && !isAgentTab  && !isBinaryTab"
        @click="initiateSaltState(true)"
        class="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="isLoading || isCommitInFlight"
        :aria-label="isLoading ? 'Loading...' : 'Initiate dry run'"
      >
        Initiate Dry Run
        {{ selectedCount > 0 ? `(${selectedCount})` : "(All)" }}
      </button>
      <button v-if="isAgentTab"
        @click="approveSelectedAgents"
        class="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="isLoading || isCommitInFlight"
        :aria-label="isLoading ? 'Loading...' : 'Approve Agents'"
      >
        Approve Agents
        {{ `(${selectedCount})`}}
      </button>
      <button v-if="isStaff && isAgentTab"
        @click="bulkApproveAgents"
        class="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="isLoading || isCommitInFlight"
        :aria-label="isLoading ? 'Loading...' : 'Approve Agents'"
      >
        Bulk Approve All Agents
      </button>
      <button v-if="isBinaryTab"
        @click="approveSelectedBinaries"
        class="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="isLoading || isCommitInFlight"
        :aria-label="isLoading ? 'Loading...' : 'Approve Agents'"
      >
        Approve Binaries
        {{ `(${selectedCount})`}}
      </button>
      <button v-if="isStaff && isBinaryTab"
        @click="bulkApproveBinaries"
        class="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="isLoading || isCommitInFlight"
        :aria-label="isLoading ? 'Loading...' : 'Approve Agents'"
      >
        Bulk Approve All Binaries
      </button>
      <button v-if="!isAgentTab  && !isBinaryTab"
        @click="initiateSaltState(false)"
        class="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="isLoading || isCommitInFlight"
        :aria-label="isLoading ? 'Loading...' : 'Initiate commit'"
      >
        Initiate Commit
        {{ selectedCount > 0 ? `(${selectedCount})` : "(All)" }}
      </button>
      <button v-if="isStaff  && !isAgentTab && !isBinaryTab"
        @click="initiateBulkCommit"
        class="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="isLoading || isCommitInFlight"
        :aria-label="isLoading ? 'Loading...' : 'Bulk commit all changes'"
      >
        Bulk Commit All Changes
      </button>
    </div>
  </div>
</template>

<script>

import { getPersistedPageSize, setPersistedPageSize } from "@/composables/usePersistedPageSize";
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
import { ChevronLeft, ChevronRight } from "lucide-vue-next";
import { getAgents, updateAgent } from "@/services/agentService";
import { getBinarys, approveBinary } from "@/services/binaryService";
import { getOrgs } from "@/services/wsOrgService";
import { getAgentGroups } from "@/services/agentGroupService";
import { useRouter, useRoute } from "vue-router";
import {
  getConfigurationChanges,
  executeConfiguration,
  getConfigurationDetails,
  executeBulkConfiguration,
  getConfigurationCounts,
  getConfigurationStatus,
  dismissConfigurationRequest,
} from "@/services/configurationService";
import { useMultiTenant } from "@/composables/useMultiTenant";
import SearchBar from "@/components/SearchBar.vue";
import GenericTable from "@/components/GenericTable.vue";
import debounce from "lodash/debounce";
import { getAllModules } from "@/services/moduleService";
import { useStore } from "@/store";
import OrganizationTabs from "@/components/OrganizationTabs.vue";
import { useTutorial } from "@/composables/tutorial";

import GlassSelect from "@/components/GlassSelect.vue";
export default {
  name: "SaltStateView",
  components: {
    SearchBar,
    GenericTable,
    OrganizationTabs,
    ChevronLeft,
    ChevronRight,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const tenant = useMultiTenant();
    const currentSchema = computed(() => route.query.schemaName || tenant.currentSchemaName?.value || null);

    const showHowToConfig = ref(false);
    const { start: _start } = useTutorial();

    const configTutorialSteps = [
      { element: '#searchbar', action: 'display', message: 'Search pending configuration changes by module name.' },
      { element: '#config-tabs', action: 'display', message: 'Switch between tabs to view pending changes by type — All, Probes, Sites, Tunnels, Loadshedding Areas, Fortigates, Endpoint Availability, and Workspace.' },
      { element: '#all-panel', action: 'display', message: 'Select items from the table to target specific records for your next dry run or commit.' },
      { element: '#config-actions', action: 'display', message: 'Initiate Dry Run previews changes without applying them. Initiate Commit pushes selected changes to production. Bulk Commit All applies every pending change at once.' },
    ];

    const startTutorial = async () => {
      await _start(configTutorialSteps);
    };

    const user = computed(() => useStore.getUser());
    const isServiceProviderUser = computed(() => useStore.isServiceProviderUser());

    const handleOrgSelect = async (orgId) => {
      await tenant.switchOrganization(orgId, async () => {
        await fetchPreview();
        await fetchConfigurationCounts();
      });
    };

    const fetchModules = async() => {
      try {
        
        const currentUser = user.value;

        if (!currentUser) {
          return;
        }

        if (currentUser?.is_staff){
          orgModules.value = superuserModules.value;

        } else{
          const allmodules = await getAllModules();
          const modules = allmodules
            .filter(module => module.uiconfig === currentUser?.org && module.enabled)
            .map(({ name }) => {
              const lowerName = name.toLowerCase();
              if (lowerName === "wifiprofile") return "wifi_profiles";
              if (lowerName === "hostavailability") return "host_availabilities";
              if (lowerName === "networkdevice") return "network_devices";
              const renamed = lowerName === "workspace" ? "agent" : lowerName;
              const plural = renamed === "blackbox" ? renamed + "es" : renamed + "s";
              return plural;
            });

          const shouldIncludeAll = modules.some(m =>
            coreModules.value.includes(m)
          );

          orgModules.value = shouldIncludeAll ? ['all', ...modules] : modules;
          activeTab.value = orgModules.value.includes('all') ? 'all' : "agents"
        }
          
      } catch (error) {
        console.error(error)
      }
    };

    const isStaff = computed(() => user.value?.is_staff);
    const isAgentTab = computed(() => activeTab.value == 'agents');
    const isBinaryTab = computed(() => activeTab.value == 'binaries');
    // Base reactive refs
    const preview = ref({
      probes: [],
      sites: [],
      tunnels: [],
      loadsheddingareas: [],
      blackboxes:[],
      alloys: [],
      wifi_profiles: [],
      captures: [],
      ssh_collectors: [],
      host_availabilities: [],
      network_devices: [],
      all:[]
    });

    const filteredProbes = computed(() => {
      let result = preview.value.probes || [];
      if (searchQuery.value) {
        const search = searchQuery.value.toLowerCase();
        result = result.filter(
          (probe) =>
            probe.name.toLowerCase().includes(search) ||
            probe.site_name?.toLowerCase().includes(search)
        );
      }
      return result;
    });

    const filteredAll = computed(() => {
      const sites = (preview.value.all || []).map((s) => ({ ...s, _type: 'site' }));
      const wifiProfiles = (preview.value.wifi_profiles || []).map((w) => ({ ...w, _type: 'wifi_profile' }));
      const captures = (preview.value.captures || []).map((c) => ({ ...c, _type: 'capture' }));
      let result = [...sites, ...wifiProfiles, ...captures];
      if (searchQuery.value) {
        const search = searchQuery.value.toLowerCase();
        result = result.filter((item) =>
          item.name.toLowerCase().includes(search)
        );
      }
      return result;
    });

    const filteredSites = computed(() => {
      let result = preview.value.sites || [];
      if (searchQuery.value) {
        const search = searchQuery.value.toLowerCase();
        result = result.filter((site) =>
          site.name.toLowerCase().includes(search)
        );
      }
      return result;
    });

    const filteredTunnels = computed(() => {
      let result = preview.value.tunnels || [];
      if (searchQuery.value) {
        const search = searchQuery.value.toLowerCase();
        result = result.filter((tunnel) =>
          tunnel.name.toLowerCase().includes(search)
        );
      }
      return result;
    });

    const filteredLoadsheddingAreas = computed(() => {
      let result = preview.value.loadsheddingareas || [];
      if (searchQuery.value) {
        const search = searchQuery.value.toLowerCase();
        result = result.filter((ld) =>
          ld.name.toLowerCase().includes(search)
        );
      }
      return result;
    });

    const filteredBlackboxes = computed(() => {
      let result = preview.value.blackboxes || [];
      if (searchQuery.value) {
        const search = searchQuery.value.toLowerCase();
        result = result.filter((blackbox) =>
          blackbox.job_name.toLowerCase().includes(search)
        );
      }
      return result;
    });

    const filteredHostAvailabilities = computed(() => {
      let result = preview.value.host_availabilities || [];
      if (searchQuery.value) {
        const search = searchQuery.value.toLowerCase();
        result = result.filter((hostAvailability) =>
          hostAvailability.job_name.toLowerCase().includes(search)
        );
      }
      return result;
    });

    const filteredNetworkDevices = computed(() => {
      let result = preview.value.network_devices || [];
      if (searchQuery.value) {
        const search = searchQuery.value.toLowerCase();
        result = result.filter((device) =>
          (device.name || "").toLowerCase().includes(search)
        );
      }
      return result;
    });


    const filteredAgents = computed(() => {
      let result = preview.value.agents || [];
      if (searchQuery.value) {
        const search = searchQuery.value.toLowerCase();
        result = result.filter((agent) =>
          agent.organization_name.toLowerCase().includes(search)
        );
      }
      return result;
    });


    const filteredBinaries = computed(() => {
      let result = preview.value.binaries || [];
      if (searchQuery.value) {
        const search = searchQuery.value.toLowerCase();
        result = result.filter((binary) =>
          binary.organization_name.toLowerCase().includes(search)
        );
      }
      return result;
    });

    const filteredAlloys = computed(() => {
      let result = preview.value.alloys || [];
      if (searchQuery.value) {
        const search = searchQuery.value.toLowerCase();
        result = result.filter((alloy) =>
          alloy.name.toLowerCase().includes(search) ||
          (alloy.linked_entity_name && alloy.linked_entity_name.toLowerCase().includes(search))
        );
      }
      return result;
    });

    const filteredWifiProfiles = computed(() => {
      let result = preview.value.wifi_profiles || [];
      if (searchQuery.value) {
        const search = searchQuery.value.toLowerCase();
        result = result.filter((profile) =>
          (profile.name && profile.name.toLowerCase().includes(search)) ||
          (profile.ssid && profile.ssid.toLowerCase().includes(search))
        );
      }
      return result;
    });

    const filteredCaptures = computed(() => {
      let result = preview.value.captures || [];
      if (searchQuery.value) {
        const search = searchQuery.value.toLowerCase();
        result = result.filter((cap) =>
          (cap.name && cap.name.toLowerCase().includes(search)) ||
          (cap.probe_name && cap.probe_name.toLowerCase().includes(search)) ||
          (cap.interface && cap.interface.toLowerCase().includes(search))
        );
      }
      return result;
    });

    const filteredSshCollectors = computed(() => {
      let result = preview.value.ssh_collectors || [];
      if (searchQuery.value) {
        const search = searchQuery.value.toLowerCase();
        result = result.filter((cfg) =>
          (cfg.name && cfg.name.toLowerCase().includes(search)) ||
          (cfg.linked_entity_name && cfg.linked_entity_name.toLowerCase().includes(search))
        );
      }
      return result;
    });

    const searchQuery = ref("");
    const error = ref(null);
    const isLoading = ref(false);
    const activeTab = ref('all');
    const currentPage = ref(1);
    const itemsPerPage = ref(getPersistedPageSize());
    const selectedProbes = ref([]);
    const selectedSites = ref([]);
    const selectedAll = ref([]);
    const selectedTunnels = ref([]);
    const selectedLoadsheddingAreas = ref([]);
    const selectedBlackboxes = ref([]);
    const selectedAlloys = ref([]);
    const selectedWifiProfiles = ref([]);
    const selectedCaptures = ref([]);
    const selectedSshCollectors = ref([]);
    const selectedHostAvailabilities = ref([]);
    const selectedNetworkDevices = ref([]);
    const selectedAgents = ref([]);
    const selectedBinaries = ref([]);
    const allSelectedProbes = ref([]);
    const allSelectedSites = ref([]);
    const allSelectedAll = ref([]);
    const allSelectedTunnels = ref([]);
    const allSelectedLoadsheddingAreas = ref([]);
    const allSelectedBlackboxes = ref([]);
    const allSelectedAlloys = ref([]);
    const allSelectedWifiProfiles = ref([]);
    const allSelectedCaptures = ref([]);
    const allSelectedSshCollectors = ref([]);
    const allSelectedHostAvailabilities = ref([]);
    const allSelectedNetworkDevices = ref([]);
    const allSelectedAgents = ref([]);
    const allSelectedBinaries = ref([])
    // configStatus mirrors the SaltStateFileRequest status enum:
    //   queued | running | completed | failed | abandoned
    // plus the legacy 'pending' that older code paths still emit. Approve
    // buttons are disabled only while a commit is genuinely in flight; for
    // failed/abandoned the banner above surfaces retry+dismiss instead.
    const configStatus = ref(null);
    const latestFileRequest = ref(null);
    const isDismissing = ref(false);
    const isCommitInFlight = computed(() => {
      return (
        configStatus.value === "pending" ||
        configStatus.value === "queued" ||
        configStatus.value === "running"
      );
    });
    const pollInterval = ref(null);
    const orgModules = ref([]);
    const superuserModules = ref([
      'all',
      'blackboxes',
      'probes',
      'sites',
      'tunnels',
      'loadsheddingareas',
      'alloys',
      'wifi_profiles',
      'captures',
      'ssh_collectors',
      'host_availabilities',
      'network_devices',
      'agents',
      'binaries'
    ]);
    const coreModules = ref([
      'blackboxes',
      'probes',
      'sites',
      'tunnels',
      'loadsheddingareas',
      'alloys',
      'wifi_profiles',
      'captures',
      'ssh_collectors',
    ]);

    const orgs = ref([]);
    const agents = ref([]);
    const binaries = ref([]);

    const totalItems = computed(() => ({
      probes: filteredProbes.value.length,
      sites: filteredSites.value.length,
      tunnels: filteredTunnels.value.length,
      loadsheddingareas: filteredLoadsheddingAreas.value.length,
      blackboxes: filteredBlackboxes.value.length,
      alloys: filteredAlloys.value.length,
      wifi_profiles: filteredWifiProfiles.value.length,
      captures: filteredCaptures.value.length,
      ssh_collectors: filteredSshCollectors.value.length,
      host_availabilities: filteredHostAvailabilities.value.length,
      network_devices: filteredNetworkDevices.value.length,
      agents: filteredAgents.value.length,
      binaries: filteredBinaries.value.length,
      all: filteredAll.value.length
    }));

    const counts = ref({
      probes: 0,
      sites: 0,
      tunnels: 0,
      loadsheddingareas: 0,
      blackboxes: 0,
      alloys: 0,
      wifi_profiles: 0,
      captures: 0,
      ssh_collectors: 0,
      host_availabilities: 0,
      network_devices: 0,
      agents: 0,
      binaries: 0,
      all: 0
    });

    // Utility functions
    const formatDateTime = (dateString) => {
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    };

    const paginatedProbes = computed(() => {
      if (itemsPerPage.value === -1) return filteredProbes.value;
      const start = (currentPage.value - 1) * itemsPerPage.value;
      return filteredProbes.value.slice(start, start + itemsPerPage.value);
    });

    const paginatedSites = computed(() => {
      if (itemsPerPage.value === -1) return filteredSites.value;
      const start = (currentPage.value - 1) * itemsPerPage.value;
      return filteredSites.value.slice(start, start + itemsPerPage.value);
    });

    const paginatedAll = computed(() => {
      if (itemsPerPage.value === -1) return filteredAll.value;
      const start = (currentPage.value - 1) * itemsPerPage.value;
      return filteredAll.value.slice(start, start + itemsPerPage.value);
    });


    const paginatedTunnels = computed(() => {
      if (itemsPerPage.value === -1) return filteredTunnels.value;
      const start = (currentPage.value - 1) * itemsPerPage.value;
      return filteredTunnels.value.slice(start, start + itemsPerPage.value);
    });

    const paginatedLoadsheddingAreas = computed(() => {
      if (itemsPerPage.value === -1) return filteredLoadsheddingAreas.value;
      const start = (currentPage.value - 1) * itemsPerPage.value;
      return filteredLoadsheddingAreas.value.slice(start, start + itemsPerPage.value);
    });

    const paginatedBlackboxes = computed(() => {
      if (itemsPerPage.value === -1) return filteredBlackboxes.value;
      const start = (currentPage.value - 1) * itemsPerPage.value;
      return filteredBlackboxes.value.slice(start, start + itemsPerPage.value);
    });

    const paginatedHostAvailabilities = computed(() => {
      if (itemsPerPage.value === -1) return filteredHostAvailabilities.value;
      const start = (currentPage.value - 1) * itemsPerPage.value;
      return filteredHostAvailabilities.value.slice(start, start + itemsPerPage.value);
    });

    const paginatedNetworkDevices = computed(() => {
      if (itemsPerPage.value === -1) return filteredNetworkDevices.value;
      const start = (currentPage.value - 1) * itemsPerPage.value;
      return filteredNetworkDevices.value.slice(start, start + itemsPerPage.value);
    });

    const paginatedAlloys = computed(() => {
      if (itemsPerPage.value === -1) return filteredAlloys.value;
      const start = (currentPage.value - 1) * itemsPerPage.value;
      return filteredAlloys.value.slice(start, start + itemsPerPage.value);
    });

    const paginatedWifiProfiles = computed(() => {
      if (itemsPerPage.value === -1) return filteredWifiProfiles.value;
      const start = (currentPage.value - 1) * itemsPerPage.value;
      return filteredWifiProfiles.value.slice(start, start + itemsPerPage.value);
    });

    const paginatedCaptures = computed(() => {
      if (itemsPerPage.value === -1) return filteredCaptures.value;
      const start = (currentPage.value - 1) * itemsPerPage.value;
      return filteredCaptures.value.slice(start, start + itemsPerPage.value);
    });

    const paginatedSshCollectors = computed(() => {
      if (itemsPerPage.value === -1) return filteredSshCollectors.value;
      const start = (currentPage.value - 1) * itemsPerPage.value;
      return filteredSshCollectors.value.slice(start, start + itemsPerPage.value);
    });

    const paginatedAgents = computed(() => {
      if (itemsPerPage.value === -1) return filteredAgents.value;
      const start = (currentPage.value - 1) * itemsPerPage.value;
      return filteredAgents.value.slice(start, start + itemsPerPage.value);
    });


    const paginatedBinaries = computed(() => {
      if (itemsPerPage.value === -1) return filteredBinaries.value;
      const start = (currentPage.value - 1) * itemsPerPage.value;
      return filteredBinaries.value.slice(start, start + itemsPerPage.value);
    });

    // Selection computed properties
    const isAllProbesSelected = computed(() => {
      return (
        paginatedProbes.value.length > 0 &&
        selectedProbes.value.length === paginatedProbes.value.length &&
        paginatedProbes.value.every((probe) =>
          selectedProbes.value.some((selected) => selected.id === probe.id)
        )
      );
    });

    const isAllSitesSelected = computed(() => {
      return (
        paginatedSites.value.length > 0 &&
        selectedSites.value.length === paginatedSites.value.length &&
        paginatedSites.value.every((site) =>
          selectedSites.value.some((selected) => selected.id === site.id)
        )
      );
    });

    const isAllSelected = computed(() => {
      return (
        paginatedAll.value.length > 0 &&
        allSelectedAll.value.length === paginatedAll.value.length &&
        paginatedAll.value.every((site) =>
          selectedAll.value.some((selected) => selected.id === site.id)
        )
      );
    });

    const isAllTunnelsSelected = computed(() => {
      return (
        paginatedTunnels.value.length > 0 &&
        selectedTunnels.value.length === paginatedTunnels.value.length &&
        paginatedTunnels.value.every((tunnel) =>
          selectedTunnels.value.some((selected) => selected.id === tunnel.id)
        )
      );
    });

    const isAllLoadsheddingAreasSelected = computed(() => {
      return (
        paginatedLoadsheddingAreas.value.length > 0 &&
        selectedLoadsheddingAreas.value.length === paginatedLoadsheddingAreas.value.length &&
        paginatedLoadsheddingAreas.value.every((loadshedding) =>
          selectedLoadsheddingAreas.value.some((selected) => selected.id === loadshedding.id)
        )
      );
    });

    const isAllBlackboxesSelected = computed(() => {
      return (
        paginatedBlackboxes.value.length > 0 &&
        selectedBlackboxes.value.length === paginatedBlackboxes.value.length &&
        paginatedBlackboxes.value.every((blackbox) =>
          selectedBlackboxes.value.some((selected) => selected.id === blackbox.id)
        )
      );
    });

    const isAllHostAvailabilitiesSelected = computed(() => {
      return (
        paginatedHostAvailabilities.value.length > 0 &&
        selectedHostAvailabilities.value.length === paginatedHostAvailabilities.value.length &&
        paginatedHostAvailabilities.value.every((hostAvailability) =>
          selectedHostAvailabilities.value.some((selected) => selected.id === hostAvailability.id)
        )
      );
    });

    const isAllNetworkDevicesSelected = computed(() => {
      return (
        paginatedNetworkDevices.value.length > 0 &&
        selectedNetworkDevices.value.length === paginatedNetworkDevices.value.length &&
        paginatedNetworkDevices.value.every((device) =>
          selectedNetworkDevices.value.some((selected) => selected.id === device.id)
        )
      );
    });

    const isAllAlloysSelected = computed(() => {
      return (
        paginatedAlloys.value.length > 0 &&
        selectedAlloys.value.length === paginatedAlloys.value.length &&
        paginatedAlloys.value.every((alloy) =>
          selectedAlloys.value.some((selected) => selected.id === alloy.id)
        )
      );
    });

    const isAllWifiProfilesSelected = computed(() => {
      return (
        paginatedWifiProfiles.value.length > 0 &&
        selectedWifiProfiles.value.length === paginatedWifiProfiles.value.length &&
        paginatedWifiProfiles.value.every((profile) =>
          selectedWifiProfiles.value.some((selected) => selected.id === profile.id)
        )
      );
    });

    const isAllCapturesSelected = computed(() => {
      return (
        paginatedCaptures.value.length > 0 &&
        selectedCaptures.value.length === paginatedCaptures.value.length &&
        paginatedCaptures.value.every((cap) =>
          selectedCaptures.value.some((selected) => selected.id === cap.id)
        )
      );
    });

    const isAllSshCollectorsSelected = computed(() => {
      return (
        paginatedSshCollectors.value.length > 0 &&
        selectedSshCollectors.value.length === paginatedSshCollectors.value.length &&
        paginatedSshCollectors.value.every((cfg) =>
          selectedSshCollectors.value.some((selected) => selected.id === cfg.id)
        )
      );
    });

    const isAllAgentsSelected = computed(() => {
      return (
        paginatedAgents.value.length > 0 &&
        selectedAgents.value.length === paginatedAgents.value.length &&
        paginatedAgents.value.every((agent) =>
          selectedAgents.value.some((selected) => selected.licensekey === agent.licensekey)
        )
      );
    });


    const isAllBinariesSelected = computed(() => {
      return (
        paginatedBinaries.value.length > 0 &&
        selectedBinaries.value.length === paginatedBinaries.value.length &&
        paginatedBinaries.value.every((binary) =>
          selectedBinaries.value.some(
            (selected) => selected.licensekey === binary.licensekey
          )
        )
      );
    });

    const totalPages = computed(() => {
      if (itemsPerPage.value === -1) return {
          probes: 1,
          sites: 1,
          tunnels: 1,
          loadsheddingareas: 1,
          blackboxes: 1,
          alloys: 1,
          wifi_profiles: 1,
          captures: 1,
          ssh_collectors: 1,
          host_availabilities: 1,
          network_devices: 1,
          all: 1,
          agents: 1,
          binaries: 0
        };
      return {
        probes: Math.ceil(filteredProbes.value.length / itemsPerPage.value) || 1,
        sites: Math.ceil(filteredSites.value.length / itemsPerPage.value) || 1,
        tunnels: Math.ceil(filteredTunnels.value.length / itemsPerPage.value) || 1,
        loadsheddingareas: Math.ceil(filteredLoadsheddingAreas.value.length / itemsPerPage.value) || 1,
        blackboxes: Math.ceil(filteredBlackboxes.value.length / itemsPerPage.value) || 1,
        alloys: Math.ceil(filteredAlloys.value.length / itemsPerPage.value) || 1,
        wifi_profiles: Math.ceil(filteredWifiProfiles.value.length / itemsPerPage.value) || 1,
        captures: Math.ceil(filteredCaptures.value.length / itemsPerPage.value) || 1,
        ssh_collectors: Math.ceil(filteredSshCollectors.value.length / itemsPerPage.value) || 1,
        host_availabilities: Math.ceil(filteredHostAvailabilities.value.length / itemsPerPage.value) || 1,
        network_devices: Math.ceil(filteredNetworkDevices.value.length / itemsPerPage.value) || 1,
        all: Math.ceil(filteredAll.value.length / itemsPerPage.value) || 1,
        agents: Math.ceil(filteredAgents.value.length / itemsPerPage.value) || 1,
        binaries: Math.ceil(filteredBinaries.value.length / itemsPerPage.value) || 1,
      };
    });

    const selectedCount = computed(() => {
      return (
        selectedProbes.value.length +
        selectedSites.value.length +
        selectedTunnels.value.length +
        selectedLoadsheddingAreas.value.length +
        selectedBlackboxes.value.length +
        selectedAlloys.value.length +
        selectedWifiProfiles.value.length +
        selectedCaptures.value.length +
        selectedSshCollectors.value.length +
        selectedHostAvailabilities.value.length +
        selectedNetworkDevices.value.length +
        selectedAll.value.length +
        selectedAgents.value.length +
        selectedBinaries.value.length
      );
    });

    const isFiltered = computed(() => searchQuery.value !== "");

    // Methods
    const toggleAllProbes = () => {
      if (isAllProbesSelected.value) {
        selectedProbes.value = [];
      } else {
        selectedProbes.value = [...paginatedProbes.value];
      }
    };

    const toggleAllSites = () => {
      if (isAllSitesSelected.value) {
        selectedSites.value = [];
      } else {
        selectedSites.value = [...paginatedSites.value];
      }
    };

    const toggleAll = () => {
      if (isAllSelected.value) {
        selectedAll.value = [];
      } else {
        selectedAll.value = [...paginatedAll.value];
      }
    };

    const toggleAllTunnels = () => {
      if (isAllTunnelsSelected.value) {
        selectedTunnels.value = [];
      } else {
        selectedTunnels.value = [...paginatedTunnels.value];
      }
    };

    const toggleAllLoadSheddingAreas = () => {
      if (isAllLoadsheddingAreasSelected.value) {
        selectedLoadsheddingAreas.value = [];
      } else {
        selectedLoadsheddingAreas.value = [...paginatedLoadsheddingAreas.value];
      }
    };

    const toggleAllBlackboxes = () => {
      if (isAllBlackboxesSelected.value) {
        selectedBlackboxes.value = [];
      } else {
        selectedBlackboxes.value = [...paginatedBlackboxes.value];
      }
    };

    const toggleAllHostAvailabilities = () => {
      if (isAllHostAvailabilitiesSelected.value) {
        selectedHostAvailabilities.value = [];
      } else {
        selectedHostAvailabilities.value = [...paginatedHostAvailabilities.value];
      }
    };

    const toggleAllNetworkDevices = () => {
      if (isAllNetworkDevicesSelected.value) {
        selectedNetworkDevices.value = [];
      } else {
        selectedNetworkDevices.value = [...paginatedNetworkDevices.value];
      }
    };

    const toggleAllAlloys = () => {
      if (isAllAlloysSelected.value) {
        selectedAlloys.value = [];
      } else {
        selectedAlloys.value = [...paginatedAlloys.value];
      }
    };

    const toggleAllWifiProfiles = () => {
      if (isAllWifiProfilesSelected.value) {
        selectedWifiProfiles.value = [];
      } else {
        selectedWifiProfiles.value = [...paginatedWifiProfiles.value];
      }
    };

    const toggleAllSshCollectors = () => {
      if (isAllSshCollectorsSelected.value) {
        selectedSshCollectors.value = [];
      } else {
        selectedSshCollectors.value = [...paginatedSshCollectors.value];
      }
    };

    const toggleAllCaptures = () => {
      if (isAllCapturesSelected.value) {
        selectedCaptures.value = [];
      } else {
        selectedCaptures.value = [...paginatedCaptures.value];
      }
    };

    const toggleAllAgents = () => {
      if (isAllAgentsSelected.value) {
        selectedAgents.value = [];
      } else {
        selectedAgents.value = [...paginatedAgents.value];
      }
    };


    const toggleAllBinaries = () => {
      if (isAllBinariesSelected.value) {
        selectedBinaries.value = [];
      } else {
        selectedBinaries.value = [...paginatedBinaries.value];
      }
    };

    const clearSelection = () => {
      switch (activeTab.value) {
        case "probes":
          selectedProbes.value = [];
          break;
        case "sites":
          selectedSites.value = [];
          break;
        case "all":
          selectedAll.value = [];
          break;
        case "tunnels":
          selectedTunnels.value = [];
          break;
        case "loadsheddingareas":
          selectedLoadsheddingAreas.value = [];
          break;  
        case "blackboxes":
          selectedBlackboxes.value = [];
          break;
        case "alloys":
          selectedAlloys.value = [];
          break;
        case "wifi_profiles":
          selectedWifiProfiles.value = [];
          break;
        case "captures":
          selectedCaptures.value = [];
          break;
        case "ssh_collectors":
          selectedSshCollectors.value = [];
          break;
        case "host_availabilities":
          selectedHostAvailabilities.value = [];
          break;
        case "network_devices":
          selectedNetworkDevices.value = [];
          break;
        case "agents":
          selectedAgents.value = [];
          break;
        case "binaries":
          selectedBinaries.value = [];
          break;
      }
    };

    const clearAllSelections = () => {
      allSelectedProbes.value = [];
      allSelectedSites.value = [];
      allSelectedAll.value = [];
      allSelectedTunnels.value = [];
      allSelectedLoadsheddingAreas.value = [];
      allSelectedBlackboxes.value = [];
      allSelectedAlloys.value = [];
      allSelectedWifiProfiles.value = [];
      allSelectedCaptures.value = [];
      allSelectedSshCollectors.value = [];
      allSelectedHostAvailabilities.value = [];
      allSelectedNetworkDevices.value = [];
      allSelectedAgents.value = [];
      allSelectedBinaries.value = [];

      selectedProbes.value = [];
      selectedSites.value = [];
      selectedAll.value = [];
      selectedTunnels.value = [];
      selectedLoadsheddingAreas.value = [];
      selectedBlackboxes.value = [];
      selectedAlloys.value = [];
      selectedWifiProfiles.value = [];
      selectedCaptures.value = [];
      selectedSshCollectors.value = [];
      selectedHostAvailabilities.value = [];
      selectedNetworkDevices.value = [];
      selectedAgents.value = [];
      selectedBinaries.value = [];
    };

    const handleSelection = (type, items) => {
      switch (type) {
        case "probes":
          selectedProbes.value = items;
          break;
        case "sites":
          selectedSites.value = items;
          break;
        case "all":
          selectedAll.value = items;
          break;  
        case "tunnels":
          selectedTunnels.value = items;
          break;
        case "loadsheddingareas":
          selectedLoadsheddingAreas.value = items;
          break;  
        case "blackboxes":
          selectedBlackboxes.value = items;
          break;
        case "alloys":
          selectedAlloys.value = items;
          break;
        case "wifi_profiles":
          selectedWifiProfiles.value = items;
          break;
        case "captures":
          selectedCaptures.value = items;
          break;
        case "ssh_collectors":
          selectedSshCollectors.value = items;
          break;
        case "host_availabilities":
          selectedHostAvailabilities.value = items;
          break;
        case "network_devices":
          selectedNetworkDevices.value = items;
          break;
        case "agents":
          selectedAgents.value = items;
          break;
        case "binaries":
          selectedBinaries.value = items;
          break;
      }
    };

    const handleSelectionChange = (type, items) => {
      switch (type) {
        case "probes":
          allSelectedProbes.value = items;
          break;
        case "sites":
          allSelectedSites.value = items;
          break;
        case "all":
          allSelectedAll.value = items;
          break;  
        case "tunnels":
          allSelectedTunnels.value = items;
          break;
        case "loadsheddingareas":
          allSelectedLoadsheddingAreas.value = items;
          break;  
        case "blackboxes":
          allSelectedBlackboxes.value = items;
          break;
        case "alloys":
          allSelectedAlloys.value = items;
          break;
        case "wifi_profiles":
          allSelectedWifiProfiles.value = items;
          break;
        case "captures":
          allSelectedCaptures.value = items;
          break;
        case "ssh_collectors":
          allSelectedSshCollectors.value = items;
          break;
        case "host_availabilities":
          allSelectedHostAvailabilities.value = items;
          break;
        case "network_devices":
          allSelectedNetworkDevices.value = items;
          break;
        case "agents":
          allSelectedAgents.value = items;
          break;
        case "binaries":
          allSelectedBinaries.value = items;
          break;
      }
    };

    const handleRowClick = (item) => {
      // Determine the type from activeTab (remove the 's' from the end)
      const tab = activeTab.value;
      let type;

      if (tab === "all") {
        // The All tab mixes sites + wifi_profiles + captures; dispatch by
        // item._type so we land on the correct details endpoint. Leaving it
        // as "all" sends everything through the site-cascade path, which
        // 404s for non-site pks (seen as `/details/site/<pk> not found`).
        if (item._type === "wifi_profile") type = "wifiprofile";
        else if (item._type === "capture") type = "capture";
        else type = "all";
      } else if (tab === "blackboxes") {
        type = "blackbox";
      } else if (tab === "binaries") {
        type = "binary";
      } else if (tab === "ssh_collectors") {
        type = "sshcollector";
      } else if (tab === "host_availabilities") {
        type = "hostavailability";
      } else if (tab === "network_devices") {
        type = "networkdevice";
      }
      else {
        type = tab.endsWith("s") ? tab.slice(0, -1) : tab;
      }

      router.push({
        name: "ConfigurationDetails",
        params: {
          type: type,
          id: item.id || item.licensekey,
        },
        query: currentSchema.value ? { schemaName: currentSchema.value } : {},
      });
    };

    const stopPolling = () => {
      if (pollInterval.value) {
        clearInterval(pollInterval.value);
        pollInterval.value = null;
      }
    };

    const pollConfigurationStatus = async (fileRequestId) => {
      try {
        const response = await getConfigurationStatus(fileRequestId, currentSchema.value);
        configStatus.value = response.status;
        // Keep latestFileRequest in sync so the abandoned/failed banner has
        // the freshest error message + timestamps to render.
        latestFileRequest.value = {
          id: fileRequestId,
          status: response.status,
          created_date: response.created_date,
          last_heartbeat_at: response.last_heartbeat_at,
          error_message: response.error_message,
        };

        // Stop polling once the row reaches any terminal state. abandoned is
        // terminal too (reaper-set; only manual retry can move it forward).
        const terminal = ["completed", "failed", "abandoned"];
        if (terminal.includes(response.status)) {
          stopPolling();

          await fetchPreview();
          await fetchAgents();
          await fetchBinaries();
        }
      } catch (err) {
        console.error("Error polling configuration status:", err);
        stopPolling();
      }
    };

    const startStatusPolling = (fileRequestId) => {
      stopPolling(); // Clear any existing polling
      pollInterval.value = setInterval(() => {
        pollConfigurationStatus(fileRequestId);
      }, 5000);
    };

    const fetchConfigurationCounts = async () => {
      try {
        const response = await getConfigurationCounts(currentSchema.value);
        counts.value = response.counts;
      } catch (err) {
        console.error("Error fetching configuration counts:", err);
        error.value =
          err.response?.data?.message ||
          "Failed to fetch configuration counts.";
      }
    };


    const fetchAgents = async () => {
      try {
        const response = await getAgents();
        orgs.value = await getOrgs();
  
        response.forEach(agent => {
            agent.organization_name = orgs.value.find(item => item.id === agent.organization)?.name;
            agent.hostname = agent.hostname || 'unregistered';
            agent.username = agent.username || 'unregistered';
        });

        agents.value = response.filter(item => item.license_state === 'Unapproved' || item.license_state === 'Pending')

        preview.value = {...preview.value, agents: agents.value}
        console.log(preview.value)
      } catch (error) {
        
      }
    };


    const fetchBinaries = async () => {
      try {
        const response = await getBinarys();
        const orgs = await getOrgs();
  
        const agents = await getAgents();
        const groups = await getAgentGroups();

        const orgMap = Object.fromEntries(orgs.map(org => [org.id, org.name]));
        const agentMap = Object.fromEntries(agents.map(agent => [agent.licensekey, agent.organization]));
        const groupMap = Object.fromEntries(groups.map(group => [group.id, group.organization]));

        binaries.value = response.map(binary => {
          let orgName = null;

          if (binary?.organization) orgName = orgMap[binary.organization];
          if (binary?.agent && agentMap[binary.agent]) orgName = orgMap[agentMap[binary.agent]];
          if (binary?.group && groupMap[binary.group]) orgName = orgMap[groupMap[binary.group]];

          let binary_scope = "unknown";
            if (binary.organization && !binary.group && !binary.agent) {
              binary_scope = `Organization`;
            } else if (binary.organization && binary.group && !binary.agent) {
              binary_scope = `Group ${groups.find(item => item.id == binary.group)?.name}`;
            } else if (binary.organization && binary.agent) {
              binary_scope = `Agent ${agents.find(item => item.licensekey == binary.agent)?.licensekey}`;
            }
          
          return {
            ...binary,
            organization_name: orgName,
            binary_label: `${binary?.binary_type[0].toUpperCase()}${binary?.binary_type.slice(1)}`,
            binary_scope
          };
        })

        binaries.value = binaries.value.filter(item => item.status === 'pending')
        preview.value = {...preview.value, binaries: binaries.value}

      } catch (error) {
        
      }
    };

    // Ensure `fetchPreview` is only defined once
    const fetchPreview = async () => {
      isLoading.value = true;
      try {
        error.value = null;
        const response = await getConfigurationChanges(searchQuery.value, currentSchema.value);

        preview.value = {
          ...preview.value,
          probes: response.probes.results,
          sites: response.sites.results,
          tunnels: response.tunnels.results,
          loadsheddingareas: response.loadsheddingareas.results,
          blackboxes: response.blackboxes.results,
          alloys: response.alloys?.results || [],
          wifi_profiles: response.wifi_profiles?.results || [],
          captures: response.captures?.results || [],
          ssh_collectors: response.ssh_collectors?.results || [],
          host_availabilities: response.host_availabilities?.results || [],
          network_devices: response.network_devices?.results || [],
          all: response.all.results
        };
        // Update status if available. Prefer the explicit status enum
        // (queued/running/completed/failed/abandoned); fall back to the
        // legacy success boolean for older backends. This is what surfaces
        // the abandoned/failed retry banner.
        if (response.latest_file_request) {
          latestFileRequest.value = response.latest_file_request;
          if (response.latest_file_request.status) {
            configStatus.value = response.latest_file_request.status;
          } else if (response.latest_file_request.success !== undefined) {
            configStatus.value = response.latest_file_request.success
              ? "completed"
              : "pending";
          }
        }

        // Reset pagination when data changes
        currentPage.value = 1;
      } catch (err) {
        console.error("Error fetching configuration changes:", err);
        error.value =
          err.response?.data?.message ||
          "Failed to fetch configuration changes. Please try again later.";
        preview.value = { probes: [], sites: [], tunnels: [], loadsheddingareas: [], blackboxes: [], alloys: [], wifi_profiles: [], captures: [], ssh_collectors: [], host_availabilities: [], network_devices: [] };
      } finally {
        isLoading.value = false;
      }
    };

    const debouncedSearch = computed(() => {
      currentPage.value = 1;
      return searchQuery.value;
    });

    watch(
      debouncedSearch,
      () => {
        // The filtered computed properties will automatically update
      },
      { immediate: true }
    );

    watch(
      () => user.value,
      (newVal) => {
        if (newVal) {
          fetchModules();
        }
      },
      { immediate: true }
    );

    const initiateSaltState = async (isDryRun) => {
      // When nothing is explicitly selected the button reads
      // "Initiate Commit (All)" — but executeConfiguration with empty arrays
      // submits null for every type, so the backend's execute() doesn't iterate
      // pending alloy configs and never queues any probe for highstate. The
      // user has been seeing pillar regenerate correctly while probes silently
      // miss the state.apply because of this gap. Delegate to bulk_execute
      // (which scans every model for pending rows and includes their probes
      // in probes_serializable) when nothing is selected, matching what the
      // "(All)" label promises.
      if (selectedCount.value === 0 && !isDryRun) {
        await initiateBulkCommit();
        return;
      }
      isLoading.value = true;
      try {
        error.value = null;
        const selectedProbeIds = selectedProbes.value.map((probe) => probe.id);
        const selectedSiteIds = selectedSites.value.map((site) => site.id);
        const selectedTunnelIds = selectedTunnels.value.map((tunnel) => tunnel.id);
        const selectedLoadsheddingAreaIds = selectedLoadsheddingAreas.value
                                            .map((loadshedding) => loadshedding.id)
        const selectedBlackboxIds = selectedBlackboxes.value.map((blackbox) => blackbox.id)
        const selectedAlloyIds = selectedAlloys.value.map((alloy) => alloy.id)
        const selectedSshCollectorIds = selectedSshCollectors.value.map((cfg) => cfg.id)
        const selectedHostAvailabilityIds = selectedHostAvailabilities.value.map((hostAvailability) => hostAvailability.id)
        const selectedNetworkDeviceIds = selectedNetworkDevices.value.map((device) => device.id)
        const selectedWifiProfileIds = [...selectedWifiProfiles.value.map((profile) => profile.id)]
        const selectedAllSites = selectedAll.value.filter((item) => item._type !== 'wifi_profile' && item._type !== 'capture')
        const selectedAllWifiProfiles = selectedAll.value.filter((item) => item._type === 'wifi_profile')
        const selectedAllCaptures = selectedAll.value.filter((item) => item._type === 'capture')
        selectedAllWifiProfiles.forEach((item) => {
          if (!selectedWifiProfileIds.includes(item.id)) selectedWifiProfileIds.push(item.id)
        })
        const selectedCaptureIds = selectedAllCaptures.map((item) => item.id)
        const selectedAllIds = selectedAllSites.map((item) => item.id)

        const response = await executeConfiguration(
          isDryRun,
          selectedProbeIds.length > 0 ? selectedProbeIds : null,
          selectedSiteIds.length > 0 ? selectedSiteIds : null,
          selectedTunnelIds.length > 0 ? selectedTunnelIds : null,
          selectedLoadsheddingAreaIds.length > 0 ? selectedLoadsheddingAreaIds : null,
          selectedBlackboxIds.length > 0 ? selectedBlackboxIds: null,
          selectedAllIds.length > 0 ? selectedAllIds : null,
          selectedAlloyIds.length > 0 ? selectedAlloyIds : null,
          selectedWifiProfileIds.length > 0 ? selectedWifiProfileIds : null,
          selectedCaptureIds.length > 0 ? selectedCaptureIds : null,
          selectedSshCollectorIds.length > 0 ? selectedSshCollectorIds : null,
          selectedHostAvailabilityIds.length > 0 ? selectedHostAvailabilityIds : null,
          selectedNetworkDeviceIds.length > 0 ? selectedNetworkDeviceIds : null,
          currentSchema.value,
        );

        console.log(`${isDryRun ? "Dry run" : "Commit"} response:`, response);

        if (response.file_request_id) {
          startStatusPolling(response.file_request_id);
        }

        await fetchPreview();
        await fetchAgents();
        if (!isDryRun) {
          await tenant.fetchOrganizations();
        }
      } catch (err) {
        console.error(
          `Error initiating ${isDryRun ? "dry run" : "commit"}:`,
          err
        );
        error.value =
          err.response?.data?.message ||
          `Failed to initiate ${
            isDryRun ? "dry run" : "commit"
          }. Please try again later.`;
      } finally {
        isLoading.value = false;
      }
    };


    const bulkApproveAgents = async () => {
      if (filteredAgents.value.length === 0) {
        return;
      }

      try {
        const approvePromises = filteredAgents.value.map((agent) =>
          updateAgent(agent.licensekey, {...agent, license_state: "Active"})
        );
        await Promise.all(approvePromises);

        filteredAgents.value = filteredAgents.value.filter(
          (agent) => !filteredAgents.value.some((item) => item.licensekey === agent.licensekey)
        );
        filteredAgents.value = [];
        fetchAgents();
      } catch (error) {
        console.error("Error during bulk approve:", error);
        alert(
          "An error occurred while approving. Please try again."
        );
      }
    };


    const bulkApproveBinaries = async () => {
      if (filteredBinaries.value.length === 0) {
        return;
      }

      try {
        const approvePromises = filteredBinaries.value.map((binary) =>
          approveBinary(binary.id)
        );
        await Promise.all(approvePromises);

        filteredBinaries.value = filteredBinaries.value.filter(
          (binary) => !filteredBinaries.value.some((item) => item.id === binary.id)
        );
        filteredBinaries.value = [];
        fetchBinaries();
      } catch (error) {
        console.error("Error during bulk approve:", error);
        alert(
          "An error occurred while approving. Please try again."
        );
      }
    };


    const approveSelectedAgents = async () => {
      if (selectedAgents.value.length === 0) {
        return;
      }

      try {
        const approvePromises = selectedAgents.value.map((agent) =>
          updateAgent(agent.licensekey, {...agent, license_state: "Active"})
        );
        await Promise.all(approvePromises);

        filteredAgents.value = filteredAgents.value.filter(
          (agent) => !selectedAgents.value.some((item) => item.licensekey === agent.licensekey)
        );
        selectedAgents.value = [];
        fetchAgents();
      } catch (error) {
        console.error("Error during bulk approve:", error);
        alert(
          "An error occurred while approving. Please try again."
        );
      }
    };


    const approveSelectedBinaries = async () => {
      if (selectedBinaries.value.length === 0) {
        return;
      }

      try {
        const approvePromises = selectedBinaries.value.map((binary) =>
          approveBinary(binary.id)
        );
        await Promise.all(approvePromises);

        filteredBinaries.value = filteredBinaries.value.filter(
          (binary) => !selectedBinaries.value.some((item) => item.id === binary.id)
        );
        selectedBinaries.value = [];
        fetchBinaries();
      } catch (error) {
        console.error("Error during bulk approve:", error);
        alert(
          "An error occurred while approving. Please try again."
        );
      }
    };


    const dismissLatestRequest = async () => {
      // Clear an abandoned/failed SaltStateFileRequest so the FE re-enables
      // approve buttons without the operator needing to manage DB state.
      if (!latestFileRequest.value?.id) return;
      isDismissing.value = true;
      try {
        await dismissConfigurationRequest(
          latestFileRequest.value.id,
          currentSchema.value
        );
        // Re-pull the latest_file_request from the changes endpoint so we
        // pick up whichever earlier completed row now becomes "latest".
        await fetchPreview();
      } catch (e) {
        console.error("Error dismissing configuration request:", e);
        error.value =
          e.response?.data?.error ||
          "Failed to dismiss the previous commit. Please try again.";
      } finally {
        isDismissing.value = false;
      }
    };

    const retryLatestRequest = async () => {
      // After acknowledging the failure, dismiss the previous request and
      // re-trigger the same commit. We re-use initiateSaltState(false) so
      // the user doesn't have to re-select rows; behaviour matches clicking
      // Initiate Commit fresh.
      await dismissLatestRequest();
      await initiateSaltState(false);
    };

    const initiateBulkCommit = async () => {
      if (isLoading.value) return;

      isLoading.value = true;
      error.value = null;
      configStatus.value = "pending";

      try {
        // First get initial counts to compare later
        const initialCounts = await getConfigurationCounts(currentSchema.value);

        const response = await executeBulkConfiguration(currentSchema.value);
        console.log("Bulk commit response:", response);

        if (response.file_request_id) {
          // Start polling with a shorter interval for bulk operations
          startStatusPolling(response.file_request_id);

          // Also poll the counts to see when they change
          const pollCounts = setInterval(async () => {
            try {
              const currentCounts = await getConfigurationCounts(currentSchema.value);
              // If all counts are 0, stop polling and refresh
              if (
                Object.values(currentCounts.counts).every(
                  (count) => count === 0
                )
              ) {
                clearInterval(pollCounts);
                await fetchPreview();
                await fetchAgents();
                await fetchBinaries();
                await tenant.fetchOrganizations();
                configStatus.value = "completed";
              }
            } catch (err) {
              console.error("Error polling counts:", err);
            }
          }, 2000);

          // Clean up interval after 2 minutes maximum
          setTimeout(() => {
            clearInterval(pollCounts);
          }, 120000);
        }

        await fetchPreview();
        await fetchAgents();
        await fetchBinaries();
        await tenant.fetchOrganizations();
      } catch (err) {
        console.error("Error initiating bulk commit:", err);
        error.value =
          err.message ||
          "Failed to initiate bulk commit. Please try again later.";
        configStatus.value = "failed";
      } finally {
        isLoading.value = false;
      }
    };

    const changePage = (delta) => {
      const newPage = currentPage.value + delta;
      const maxPage = totalPages.value[activeTab.value];
      if (newPage >= 1 && newPage <= maxPage) {
        currentPage.value = newPage;
      }
    };

    const updatePagination = () => {
      currentPage.value = 1;
      setPersistedPageSize(itemsPerPage.value);
    };

    const sortItems = (type) => {
      // Add your sorting logic here if needed
      console.log(`Sorting ${type} items`);
    };

    // Tab carousel scroll state
    const tabNav = ref(null);
    const tabsScrollLeft = ref(0);
    const tabsScrollWidth = ref(0);
    const tabsClientWidth = ref(0);
    let tabsResizeObserver = null;
    let tabsObservedEl = null;

    const canScrollTabsPrev = computed(() => tabsScrollLeft.value > 1);
    const canScrollTabsNext = computed(
      () => tabsScrollLeft.value + tabsClientWidth.value < tabsScrollWidth.value - 1
    );
    const tabsMarkerCount = computed(() => {
      if (!tabsClientWidth.value || !tabsScrollWidth.value) return 0;
      return Math.max(1, Math.ceil(tabsScrollWidth.value / tabsClientWidth.value));
    });
    const tabsCurrentMarker = computed(() => {
      const maxScroll = tabsScrollWidth.value - tabsClientWidth.value;
      if (maxScroll <= 0 || tabsMarkerCount.value <= 1) return 0;
      const ratio = Math.min(1, Math.max(0, tabsScrollLeft.value / maxScroll));
      return Math.round(ratio * (tabsMarkerCount.value - 1));
    });

    const measureTabs = () => {
      const el = tabNav.value;
      if (!el) return;
      tabsScrollLeft.value = el.scrollLeft;
      tabsScrollWidth.value = el.scrollWidth;
      tabsClientWidth.value = el.clientWidth;
    };

    const setupTabsScroll = () => {
      const el = tabNav.value;
      if (!el) return;
      if (!tabsResizeObserver) {
        tabsResizeObserver = new ResizeObserver(() => measureTabs());
      }
      if (tabsObservedEl !== el) {
        if (tabsObservedEl) tabsResizeObserver.unobserve(tabsObservedEl);
        tabsResizeObserver.observe(el);
        tabsObservedEl = el;
      }
      measureTabs();
    };

    const onTabsScroll = () => {
      const el = tabNav.value;
      if (!el) return;
      tabsScrollLeft.value = el.scrollLeft;
    };

    const scrollTabsByPage = (direction) => {
      const el = tabNav.value;
      if (!el) return;
      el.scrollBy({ left: direction * el.clientWidth * 0.9, behavior: 'smooth' });
    };

    const onWindowResizeTabs = () => measureTabs();

    watch(
      [orgModules, () => isLoading.value, () => error.value],
      () => nextTick(setupTabsScroll),
      { deep: true }
    );

    // Lifecycle hooks
    onMounted(async () => {
      window.addEventListener('resize', onWindowResizeTabs);
      nextTick(setupTabsScroll);
      await tenant.fetchOrganizations();
      const fetchAll = async (schemaName) => {
        await Promise.all([
            fetchPreview(schemaName),
            fetchConfigurationCounts(schemaName),
            fetchModules(),
            fetchAgents(),
            fetchBinaries(),
        ]);
      };

      if (tenant.currentOrganizationId.value) {
        await tenant.syncUrlParam();
        await fetchAll(tenant.currentSchemaName.value);
      } else {
          await tenant.loadInitialData(fetchAll);
      }
    });

    onUnmounted(() => {
      stopPolling();
      window.removeEventListener('resize', onWindowResizeTabs);
      if (tabsResizeObserver) {
        tabsResizeObserver.disconnect();
        tabsResizeObserver = null;
        tabsObservedEl = null;
      }
    });

    return {
      // Reactive refs
      preview,
      searchQuery,
      error,
      isLoading,
      activeTab,
      currentPage,
      itemsPerPage,
      selectedProbes,
      selectedSites,
      selectedAll,
      selectedTunnels,
      selectedBlackboxes,
      selectedLoadsheddingAreas,
      selectedAlloys,
      selectedWifiProfiles,
      selectedCaptures,
      selectedSshCollectors,
      selectedHostAvailabilities,
      selectedNetworkDevices,
      selectedAgents,
      selectedBinaries,
      allSelectedProbes,
      allSelectedSites,
      allSelectedAll,
      allSelectedTunnels,
      allSelectedBlackboxes,
      allSelectedLoadsheddingAreas,
      allSelectedAlloys,
      allSelectedWifiProfiles,
      allSelectedCaptures,
      allSelectedSshCollectors,
      allSelectedHostAvailabilities,
      allSelectedNetworkDevices,
      allSelectedAgents,
      allSelectedBinaries,
      configStatus,
      latestFileRequest,
      isDismissing,
      isCommitInFlight,
      dismissLatestRequest,
      retryLatestRequest,
      counts,
      totalItems,
      orgModules,
      user,
      tenant,
      currentSchema,
      isServiceProviderUser,
      handleOrgSelect,

      // Computed properties
      paginatedProbes,
      paginatedSites,
      paginatedAll,
      paginatedTunnels,
      paginatedBlackboxes,
      paginatedAlloys,
      paginatedWifiProfiles,
      paginatedCaptures,
      paginatedSshCollectors,
      paginatedHostAvailabilities,
      paginatedNetworkDevices,
      paginatedAgents,
      paginatedBinaries,
      paginatedLoadsheddingAreas,
      isAllProbesSelected,
      isAllSitesSelected,
      isAllTunnelsSelected,
      isAllBlackboxesSelected,
      isAllLoadsheddingAreasSelected,
      isAllAlloysSelected,
      isAllWifiProfilesSelected,
      isAllCapturesSelected,
      isAllSshCollectorsSelected,
      isAllHostAvailabilitiesSelected,
      isAllNetworkDevicesSelected,
      isAllSelected,
      isAllAgentsSelected,
      isAllBinariesSelected,
      totalPages,
      selectedCount,
      isFiltered,
      isStaff,
      isAgentTab,
      isBinaryTab,

      // Methods
      formatDateTime,
      toggleAllProbes,
      toggleAllSites,
      toggleAllTunnels,
      toggleAllBlackboxes,
      toggleAllLoadSheddingAreas,
      toggleAllAlloys,
      toggleAllWifiProfiles,
      toggleAllCaptures,
      toggleAllSshCollectors,
      toggleAllHostAvailabilities,
      toggleAllNetworkDevices,
      toggleAllAgents,
      toggleAllBinaries,
      toggleAll,
      clearSelection,
      clearAllSelections,
      handleSelection,
      handleSelectionChange,
      handleRowClick,
      debouncedSearch,
      initiateSaltState,
      initiateBulkCommit,
      changePage,
      updatePagination,
      sortItems,
      fetchModules,
      bulkApproveAgents,
      approveSelectedAgents,
      bulkApproveBinaries,
      approveSelectedBinaries,

      startTutorial,

      // Tabs carousel
      tabNav,
      canScrollTabsPrev,
      canScrollTabsNext,
      tabsMarkerCount,
      tabsCurrentMarker,
      onTabsScroll,
      scrollTabsByPage,

      // Constants
      tabs: [
        { id: "all", name: "All" },
        { id: "probes", name: "Probes" },
        { id: "sites", name: "Sites" },
        { id: "tunnels", name: "Tunnels" },
        { id: "loadsheddingareas", name: "Loadshedding Area" },
        { id: "blackboxes", name: "Endpoint Availability" },
        { id: "alloys", name: "Observability Configs" },
        { id: "wifi_profiles", name: "WiFi Profiles" },
        { id: "captures", name: "Captures" },
        { id: "ssh_collectors", name: "SSH Runners" },
        { id: "host_availabilities", name: "Host Availability" },
        { id: "network_devices", name: "Network Devices" },
        { id: "agents", name: "Workspace" },
        { id: "binaries", name: "Binaries"}
      ],
      siteColumns: [
        { key: "select", label: "", width: "5%", type: "checkbox" },
        { key: "name", label: "Site Name", sortable: true, width: "45%" },
        {
          key: "last_modified",
          label: "Last Modified",
          sortable: true,
          width: "45%",
        },
        { key: "pending", label: "Pending", sortable: true, width: "5%" },
      ],
      probeColumns: [
        { key: "select", label: "", width: "5%", type: "checkbox" },
        { key: "name", label: "Probe Name", sortable: true, width: "30%" },
        { key: "site_name", label: "Site", sortable: true, width: "30%" },
        {
          key: "last_modified",
          label: "Last Modified",
          sortable: true,
          width: "30%",
        },
        { key: "pending", label: "Pending", sortable: true, width: "5%" },
      ],
      tunnelColumns: [
        { key: "select", label: "", width: "5%", type: "checkbox" },
        { key: "name", label: "Tunnel Name", sortable: true, width: "45%" },
        {
          key: "last_modified",
          label: "Last Modified",
          sortable: true,
          width: "45%",
        },
        { key: "pending", label: "Pending", sortable: true, width: "5%" },
      ],
      loadsheddingareaColumns: [
        { key: "select", label: "", width: "5%", type: "checkbox" },
        { key: "name", label: "Loadshedding Area Name", sortable: true, width: "45%" },
        {
          key: "last_modified",
          label: "Last Modified",
          sortable: true,
          width: "45%",
        },
        { key: "pending", label: "Pending", sortable: true, width: "5%" },
      ],
      blackboxColumns: [
        { key: "select", label: "", width: "5%", type: "checkbox" },
        { key: "job_name", label: "Endpoint Availability Name", sortable: true, width: "45%" },
        {
          key: "last_modified",
          label: "Last Modified",
          sortable: true,
          width: "45%",
        },
        { key: "pending", label: "Pending", sortable: true, width: "5%" },
      ],
      hostAvailabilityColumns: [
        { key: "select", label: "", width: "5%", type: "checkbox" },
        { key: "job_name", label: "Host Availability Name", sortable: true, width: "45%" },
        {
          key: "last_modified",
          label: "Last Modified",
          sortable: true,
          width: "45%",
        },
        { key: "pending", label: "Pending", sortable: true, width: "5%" },
      ],
      networkDeviceColumns: [
        { key: "select", label: "", width: "5%", type: "checkbox" },
        { key: "name", label: "Device Name", sortable: true, width: "30%" },
        { key: "device_type", label: "Type", sortable: true, width: "15%" },
        {
          key: "last_modified",
          label: "Last Modified",
          sortable: true,
          width: "45%",
        },
        { key: "pending", label: "Pending", sortable: true, width: "5%" },
      ],
      alloyColumns: [
        { key: "select", label: "", width: "5%", type: "checkbox" },
        { key: "name", label: "Config Name", sortable: true, width: "30%" },
        { key: "linked_entity_name", label: "Linked To", sortable: true, width: "25%" },
        { key: "linked_entity_type", label: "Type", sortable: true, width: "10%" },
        {
          key: "last_modified",
          label: "Last Modified",
          sortable: true,
          width: "25%",
        },
        { key: "pending", label: "Pending", sortable: true, width: "5%" },
      ],
      wifiProfileColumns: [
        { key: "select", label: "", width: "5%", type: "checkbox" },
        { key: "name", label: "Name", sortable: true, width: "30%" },
        { key: "ssid", label: "SSID", sortable: true, width: "20%" },
        { key: "auth_type", label: "Auth Type", sortable: true, width: "15%" },
        { key: "last_modified", label: "Last Modified", sortable: true, width: "25%" },
        { key: "pending", label: "Pending", sortable: true, width: "5%" },
      ],
      captureColumns: [
        { key: "select", label: "", width: "5%", type: "checkbox" },
        { key: "name", label: "Name", sortable: true, width: "30%" },
        { key: "probe_name", label: "Probe", sortable: true, width: "25%" },
        { key: "interface", label: "Interface", sortable: true, width: "15%" },
        { key: "last_modified", label: "Last Modified", sortable: true, width: "20%" },
        { key: "pending", label: "Pending", sortable: true, width: "5%" },
      ],
      sshCollectorColumns: [
        { key: "select", label: "", width: "5%", type: "checkbox" },
        { key: "name", label: "Name", sortable: true, width: "30%" },
        { key: "linked_entity_name", label: "Scope", sortable: true, width: "25%" },
        { key: "linked_entity_type", label: "Type", sortable: true, width: "15%" },
        { key: "last_modified", label: "Last Modified", sortable: true, width: "20%" },
        { key: "pending", label: "Pending", sortable: true, width: "5%" },
      ],
      allColumns: [
        { key: "select", label: "", width: "5%", type: "checkbox" },
        { key: "name", label: "Site Name", sortable: true, width: "45%" },
        {
          key: "last_modified",
          label: "Last Modified",
          sortable: true,
          width: "45%",
        },
        { key: "pending", label: "Pending", sortable: true, width: "5%" },
      ],
      agentColumns: [
        { key: "select", label: "", width: "5%", type: "checkbox" },
        { key: "licensekey", label: "Agent License Key", sortable: true, width: "45%" },
        {
          key: "created_at",
          label: "Created Date",
          sortable: true,
          width: "45%",
        },
        { key: "license_state", label: "License State", sortable: true, width: "5%" },
      ],
      binaryColumns: [
        { key: "select", label: "", width: "5%", type: "checkbox" },
        { key: "binary_type", label: "Binary", sortable: true, width: "45%" },
        { key: "binary_scope", label: "Level", sortable: true, width: "45%" },
        {
          key: "last_modified",
          label: "Last Modified",
          sortable: true,
          width: "45%",
        },
        { key: "status", label: "State", sortable: true, width: "5%" },
      ]
    };
  },
};
</script>

<style scoped>
.cursor-pointer tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.02);
}
.no-scrollbar {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.tabs-scroll-marker {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background-color: rgba(255, 255, 255, 0.5);
  transition: background-color 0.2s, width 0.2s, height 0.2s;
}
.tabs-scroll-marker--active {
  width: 8px;
  height: 8px;
  background-color: #d946ef;
}
</style>
